import { useState, useEffect } from 'react';
import { Images, X } from 'lucide-react';
import axios from 'axios';
import { Dialog, DialogContent } from '../components/ui/dialog';
import SEO from '../components/SEO';
import useAnalytics from '../hooks/useAnalytics';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Gallery = () => {
  useAnalytics('gallery');
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      const response = await axios.get(`${API}/posts?limit=50`);
      setPosts(response.data);
    } catch (error) {
      console.error('Failed to fetch posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const [mediaFiles, setMediaFiles] = useState([]);

  useEffect(() => {
    const fetchMedia = async () => {
      try {
        const res = await axios.get(`${API}/gallery/images`);
        setMediaFiles(res.data);
      } catch (err) {
        console.error('Failed to fetch media:', err);
      }
    };
    fetchMedia();
  }, []);

  // Collect images from posts (only Dropbox-hosted URLs)
  const postImages = posts.reduce((acc, post) => {
    if (post.featured_image && !post.featured_image.includes('unsplash.com')) {
      acc.push({ url: post.featured_image, title: post.title, postSlug: post.slug });
    }
    if (post.gallery && post.gallery.length > 0) {
      post.gallery.forEach((img) => {
        if (!img.includes('unsplash.com')) {
          acc.push({ url: img, title: post.title, postSlug: post.slug });
        }
      });
    }
    return acc;
  }, []);

  // Combine Dropbox media files + post images, deduplicating by URL
  const seenUrls = new Set();
  const displayImages = [...mediaFiles.map(m => ({ url: m.url, title: m.name })), ...postImages].filter(img => {
    if (!img.url || seenUrls.has(img.url)) return false;
    seenUrls.add(img.url);
    return true;
  });

  return (
    <div className="min-h-screen bg-stone-50 pt-20" data-testid="gallery-page">
      <SEO
        title="Gallery"
        description="Photo gallery from Nepal — Himalayan landscapes, Kathmandu streets, and Nepali culture captured by Marta Scharle."
        url="/gallery"
      />
      {/* Header */}
      <section className="py-16 md:py-24 border-b border-stone-200">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
          <p className="text-sm tracking-widest uppercase text-red-800 font-medium mb-4">
            Visual Stories
          </p>
          <h1 className="font-serif text-5xl md:text-6xl text-stone-900 tracking-tight">
            Photo Gallery
          </h1>
          <p className="mt-6 text-stone-600 text-lg max-w-2xl">
            A curated collection of photographs from journeys across Nepal. 
            Each image tells a story of the land, its people, and their traditions.
          </p>
        </div>
      </section>

      {/* Gallery Grid */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
          {loading ? (
            <div className="flex justify-center py-20">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-800"></div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {displayImages.map((image, index) => (
                <div
                  key={index}
                  className={`group cursor-pointer overflow-hidden rounded-sm ${
                    index % 5 === 0 ? 'md:col-span-2 md:row-span-2' : ''
                  }`}
                  onClick={() => setSelectedImage(image)}
                  data-testid={`gallery-image-${index}`}
                >
                  <div className={`relative ${index % 5 === 0 ? 'aspect-square' : 'aspect-[4/3]'}`}>
                    <img
                      src={image.url}
                      alt={image.title}
                      loading="lazy"
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-stone-900/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <div className="absolute bottom-0 left-0 right-0 p-4">
                        <p className="text-stone-50 font-serif text-lg">{image.title}</p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {!loading && displayImages.length === 0 && (
            <div className="text-center py-20" data-testid="no-images-message">
              <Images size={48} className="text-stone-300 mx-auto mb-6" />
              <h2 className="font-serif text-2xl text-stone-900 mb-4">Gallery Coming Soon</h2>
              <p className="text-stone-600">
                Photos from Nepal will be added here as stories are published.
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Lightbox */}
      <Dialog open={!!selectedImage} onOpenChange={() => setSelectedImage(null)}>
        <DialogContent className="max-w-5xl bg-stone-900 border-none p-0">
          <button
            onClick={() => setSelectedImage(null)}
            className="absolute top-4 right-4 z-50 w-10 h-10 rounded-full bg-stone-800 flex items-center justify-center text-stone-300 hover:text-stone-50 transition-colors"
            data-testid="close-lightbox-btn"
          >
            <X size={20} />
          </button>
          {selectedImage && (
            <div className="relative">
              <img
                src={selectedImage.url}
                alt={selectedImage.title}
                className="w-full h-auto max-h-[80vh] object-contain"
              />
              <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-stone-900/80 to-transparent">
                <p className="text-stone-50 font-serif text-xl">{selectedImage.title}</p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Gallery;
